import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, HelpCircle } from "lucide-react";

import { API, portalHeaders } from "./helpers.js";
import { buildPortalTiles } from "./portalTiles.js";
import PortalHeader from "./components/PortalHeader.jsx";
import PortalLauncher from "./components/PortalLauncher.jsx";
import PortalMobileBar from "./components/PortalMobileBar.jsx";
import PasswordChangeModal from "./components/PasswordChangeModal.jsx";
import ReceivedTab from "./tabs/ReceivedTab.jsx";
import SendTab from "./tabs/SendTab.jsx";
import QuestionsTab from "./tabs/QuestionsTab.jsx";
import ProfileTab from "./tabs/ProfileTab.jsx";
import T777SimpleTab from "./tabs/T777SimpleTab.jsx";
import AppointmentsTab from "./tabs/AppointmentsTab.jsx";
import EntreprisePortal from "./entreprise/EntreprisePortal.jsx";
import PaiePortal from "./paie/PaiePortal.jsx";
import ImmeublePortal from "./immeuble/ImmeublePortal.jsx";
import DocumentsToSignTab from "./tabs/DocumentsToSignTab.jsx";
import FacturesTab from "./tabs/FacturesTab.jsx";
import ModeEmploiTab from "./tabs/ModeEmploiTab.jsx";
import NotificationPrefsTab from "./tabs/NotificationPrefsTab.jsx";
import DemoUpgradeCard from "./components/DemoUpgradeCard.jsx";
import InstallAppBanner from "./components/InstallAppBanner.jsx";
import PushEnableBanner from "./components/PushEnableBanner.jsx";
import ImpersonationBanner from "./components/ImpersonationBanner.jsx";
import { useDemoMode } from "@/hooks/useDemoMode";

const isPreviewHost = () =>
  window.location.hostname.includes("preview.emergentagent.com") ||
  window.location.hostname === "localhost";

// Iter97 — map tile id -> notification type pour le mark-read on open.
// On ne marque que les modules qui CONSOMMENT directement les notifs du type :
//  - documents → type=document  (l'utilisateur consulte les docs reçus)
//  - chat      → type=chat
//  - rdv       → type=appointment
// "envoyer" et "t777" ne marquent rien (l'utilisateur n'a pas "vu" les notifs documents
// juste parce qu'il telecharge un nouveau fichier).
const MODULE_TO_NOTIF_TYPE = {
  documents: "document",
  chat:      "chat",
  rdv:       "appointment",
};

const MODULE_TITLES = {
  info:             "Info",
  documents:        "Documents",
  envoyer:          "Envoyer un fichier",
  chat:             "Chat",
  rdv:              "Rendez-vous",
  t777:             "Bureau à domicile",
  travail_autonome: "Entreprise",
  paie:             "Paie",
  immeuble:         "Immeuble à revenu",
  mandats:          "Documents à signer",
  factures:         "Factures",
  notifications:    "Notifications",
  mode_emploi:      "Mode d'emploi",
};

// Batch #14.175 — Mapping module ouvert → slug du guide correspondant.
// Le ❓ discret en haut à droite de la page module ouvre directement le guide.
// `null` = pas de bouton ❓ affiché (mode_emploi EST l'aide).
const MODULE_TO_GUIDE_SLUG = {
  info:             "info",
  documents:        "documents",
  envoyer:          "envoyer",
  chat:             "chat",
  rdv:              "rdv",
  t777:             "t777",
  travail_autonome: "travail_autonome",
  paie:             "paie",
  immeuble:         "immeuble",
  notifications:    null,
  mandats:          "mandats",
  factures:         "factures",
  mode_emploi:      null,
};

export default function PortalDashboard() {
  const { portalUser } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [branding, setBranding] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [view, setView] = useState("home"); // "home" | "info" | "documents" | "envoyer" | "chat" | "rdv" | "t777" | "mandats"
  const [helpSlug, setHelpSlug] = useState(null);  // Batch #14.173 — slug pour aide contextuelle
  const [communications, setCommunications] = useState([]);
  const [badges, setBadges] = useState({ documents: 0, chat: 0, appointments: 0, total: 0 });
  const [mandatsCount, setMandatsCount] = useState(0);

  const currentYear = new Date().getFullYear();
  const primaryColor = branding?.primary_color || "#072354";
  const contactEmail = dashboard?.contact_email || branding?.contact_email;

  const loadDashboard = useCallback(async (year) => {
    try {
      const params = year ? `?tax_year=${year}` : "";
      const res = await axios.get(`${API}/portal/dashboard${params}`, { headers: portalHeaders() });
      setDashboard(res.data);
    } catch { /* silent */ }
  }, []);

  const loadBranding = useCallback(async () => {
    try {
      const username = portalUser?.portal_username || portalUser?.id;
      if (username) {
        const res = await axios.get(`${API}/portal/branding/${username}`);
        setBranding(res.data);
      }
    } catch { /* silent */ }
  }, [portalUser]);

  const loadComms = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal/communications`, { headers: portalHeaders() });
      setCommunications(res.data || []);
    } catch { /* silent */ }
  }, []);

  const loadBadges = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/notifications/count`, { headers: portalHeaders() });
      setBadges(r.data || { documents: 0, chat: 0, appointments: 0, total: 0 });
    } catch { /* silent */ }
  }, []);

  // Batch #14.17 — Compte combiné (anciens mandats Google Docs + nouveaux client_documents)
  const loadMandats = useCallback(async () => {
    try {
      const [r1, r2, r3, r4, r5] = await Promise.all([
        axios.get(`${API}/portal/signatures/mandats`, { headers: portalHeaders() }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/portal/documents-to-sign`, { headers: portalHeaders() }).catch(() => ({ data: { items: [] } })),
        // Batch #14.236 — mandats fiscaux (formulaire à remplir + signer)
        axios.get(`${API}/portal/fiscal-mandates`, { headers: portalHeaders() }).catch(() => ({ data: { items: [] } })),
        // Batch #14.239 — rapports d'impôt à signer
        axios.get(`${API}/portal/tax-reports`, { headers: portalHeaders() }).catch(() => ({ data: { items: [] } })),
        // Batch #14.240 — contrats de services à signer
        axios.get(`${API}/portal/service-contracts`, { headers: portalHeaders() }).catch(() => ({ data: { items: [] } })),
      ]);
      const legacy = (r1.data?.items || []).filter((m) => m.state === "ready_to_sign").length;
      const fresh = (r2.data?.items || []).filter((d) => d.status === "pending").length;
      const fiscal = (r3.data?.items || []).filter((f) => f.status === "pending").length;
      const taxrep = (r4.data?.items || []).filter((t) => t.status === "pending").length;
      const contracts = (r5.data?.items || []).filter((ct) => ct.status === "pending").length;
      setMandatsCount(legacy + fresh + fiscal + taxrep + contracts);
    } catch { /* silent */ }
  }, []);

  // Batch #14.12 — Auto-ouverture du module Factures au retour Stripe
  // Batch #14.168 — Auto-ouverture d'une tuile via ?view=… (ex: retour depuis /portal/immeuble/action/*)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("invoice_paid") || params.get("invoice_cancel")) {
      setView("factures");
      return;
    }
    const viewParam = params.get("view");
    if (viewParam) {
      setView(viewParam);
      // Nettoie l'URL pour éviter de re-déclencher au refresh
      window.history.replaceState({}, "", "/portal/dashboard");
    }
  }, []);

  // Chargement initial
  useEffect(() => {
    loadDashboard(currentYear);
    loadBranding();
    loadComms();
    loadBadges();
    loadMandats();
  }, [currentYear, loadDashboard, loadBranding, loadComms, loadBadges, loadMandats]);

  // Iter97 — Marquer les notifications du type concerné comme lues a l'ouverture
  const openModule = useCallback(
    async (moduleId) => {
      setView(moduleId);
      const notifType = MODULE_TO_NOTIF_TYPE[moduleId];
      if (notifType) {
        try {
          await axios.post(
            `${API}/portal/notifications/mark-read?type=${notifType}`,
            null,
            { headers: portalHeaders() }
          );
          // refresh badges
          loadBadges();
        } catch { /* silent */ }
      }
    },
    [loadBadges]
  );

  const goHome = useCallback(() => {
    setView("home");
    setHelpSlug(null);
    loadBadges();  // refresh au retour
  }, [loadBadges]);

  // Batch #14.173 — Aide contextuelle : ouvre Mode d'emploi pré-positionné sur le guide demandé
  const openHelp = useCallback((slug) => {
    setHelpSlug(slug);
    setView("mode_emploi");
  }, []);

  const client = dashboard?.client || {};
  const receivedComms = communications.filter((c) => c.direction === "sent");
  const sentComms = communications.filter((c) => c.direction === "received");
  const isPreview = isPreviewHost();
  // Iter114 — Mode demo : detecte uniquement via client.is_demo (compte fixe demo-portail).
  // En demo, les modules optionnels sont tous forces a true.
  const { isDemoMode } = useDemoMode(client);
  // Batch #14.16 — Tuile T777 (Bureau à domicile) RÉ-ACTIVÉE par défaut pour tous les clients
  // (avant : conditionnée à client.t777_enabled). Le formulaire simple est universel.
  const t777Enabled = true;
  // Batch #14.243 — Tuile « Entreprise » (clé technique travail_autonome) : matrice admin OU mode démo
  const travailAutonomeEnabled = isDemoMode || !!(dashboard?.portal_permissions?.travail_autonome);
  // Batch #14.251 — Tuile « Paie » : matrice admin (Gestion des accès) OU mode démo
  const paieEnabled = isDemoMode || !!(dashboard?.portal_permissions?.paie);
  // Phase 9 — Immeuble VS Code activé si la matrice portal_permissions le coche OU mode démo
  const immeubleEnabled = isDemoMode || !!(dashboard?.portal_permissions?.immeuble);

  // PSI "3 Bases" — matrice de permissions portail (null si client hors master → legacy).
  // En mode démo ou pour les clients legacy, on ne filtre pas (rétro-compat).
  const portalPermissions = dashboard?.portal_permissions || null;
  // Batch #14.322 — tuiles visibles (accueil + barre mobile), calculées une seule fois
  const tiles = buildPortalTiles({ badges, mandatsCount, t777Enabled, travailAutonomeEnabled, paieEnabled, immeubleEnabled, portalPermissions, isDemoMode });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 pb-20 md:pb-0" data-testid="portal-dashboard">
      {/* Batch #14.338 — « Mode Admin : vous naviguez en tant que … » */}
      {portalUser?.impersonation && <ImpersonationBanner client={portalUser} info={portalUser.impersonation} />}
      {isPreview && (
        <div className="bg-amber-500 text-white text-center text-[10px] font-bold py-0.5 sticky top-0 z-50" data-testid="preview-banner">
          PREVIEW — Environnement de test
        </div>
      )}

      <div style={isPreview ? { top: "20px", position: "sticky", zIndex: 30 } : {}}>
        <PortalHeader
          branding={branding}
          client={client}
          contactEmail={contactEmail}
          onChangePassword={() => setShowPasswordModal(true)}
          primaryColor={primaryColor}
        />
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        {/* Page d'accueil : bulles uniquement (aucune bande bleue, aucun KPI) */}
        {view === "home" && (
          <>
            {/* Batch #14.202 — CTA promotionnel affiché uniquement en mode démo */}
            {isDemoMode && <DemoUpgradeCard />}
            {/* Batch #14.244 — Invite « Ajouter à l'écran d'accueil » (mobile, hors démo) */}
            {!isDemoMode && <InstallAppBanner primaryColor={primaryColor} />}
            {/* Batch #14.263 — Notifications push sur le téléphone (geste utilisateur requis) */}
            {!isDemoMode && <PushEnableBanner clientId={portalUser?.id} primaryColor={primaryColor} />}
            <PortalLauncher
              onSelect={openModule}
              tiles={tiles}
              client={client}
              portalPermissions={portalPermissions}
            />
          </>
        )}

        {/* Vue module : bouton retour + composant */}
        {view !== "home" && (
          <div data-testid={`portal-module-${view}`}>
            <button
              onClick={goHome}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3 py-2.5 -ml-1 pr-3 transition"
              data-testid="portal-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Retour à l&apos;accueil
            </button>
            <div className="flex items-center justify-between gap-3 mb-5">
              <h2 className="text-2xl font-bold text-gray-900">{MODULE_TITLES[view]}</h2>
              {/* Batch #14.175 — Aide contextuelle discrète : ouvre le guide du module courant */}
              {MODULE_TO_GUIDE_SLUG[view] && tiles.some((t) => t.id === "mode_emploi") && (
                <button
                  type="button"
                  onClick={() => openHelp(MODULE_TO_GUIDE_SLUG[view])}
                  title={`Aide — ${MODULE_TITLES[view]}`}
                  aria-label={`Aide pour ${MODULE_TITLES[view]}`}
                  className="shrink-0 p-1.5 rounded-full text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition opacity-70 hover:opacity-100"
                  data-testid={`portal-module-help-${view}`}
                >
                  <HelpCircle className="w-5 h-5" />
                </button>
              )}
            </div>

            {view === "info" && <ProfileTab primaryColor={primaryColor} />}
            {view === "documents" && (
              <ReceivedTab receivedComms={receivedComms} primaryColor={primaryColor} onReload={loadComms} />
            )}
            {view === "envoyer" && (
              <SendTab
                sentComms={sentComms}
                primaryColor={primaryColor}
                onReload={() => { loadComms(); loadDashboard(currentYear); }}
              />
            )}
            {view === "chat" && (
              <QuestionsTab
                questions={dashboard?.questions || []}
                selectedYear={currentYear}
                primaryColor={primaryColor}
                onReload={() => loadDashboard(currentYear)}
              />
            )}
            {view === "rdv" && <AppointmentsTab primaryColor={primaryColor} />}
            {view === "mandats" && <DocumentsToSignTab primaryColor={primaryColor} />}
            {view === "factures" && <FacturesTab primaryColor={primaryColor} />}
            {view === "mode_emploi" && <ModeEmploiTab primaryColor={primaryColor} initialSlug={helpSlug} />}
            {view === "notifications" && <NotificationPrefsTab primaryColor={primaryColor} />}
            {view === "t777" && t777Enabled && (
              <T777SimpleTab primaryColor={primaryColor} />
            )}
            {view === "travail_autonome" && travailAutonomeEnabled && <EntreprisePortal primaryColor={primaryColor} />}
            {view === "paie" && paieEnabled && <PaiePortal primaryColor={primaryColor} />}
            {view === "immeuble" && immeubleEnabled && (
              // Batch #14.135 — Mode démo utilise le vrai ImmeublePortal
              // (alimenté par les données seedées via seed_demo_dataset).
              // Les writes restent bloqués par le middleware demo_portal_writes_blocker.
              <ImmeublePortal />
            )}
          </div>
        )}
      </main>

      {showPasswordModal && (
        <PasswordChangeModal onClose={() => setShowPasswordModal(false)} primaryColor={primaryColor} />
      )}

      <PortalMobileBar tiles={tiles} tabIds={branding?.portal_mobile_tabs} view={view} onHome={goHome} onSelect={openModule} primaryColor={primaryColor} />
    </div>
  );
}
